import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect } from "react";
import BlueButton from "../../components/BlueButton/BlueButton";
import AddGif from "./../../assets/add-gif.gif";
import SearchIcon from "@mui/icons-material/Search";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import { DataGrid } from "@mui/x-data-grid";
import { ServiceColumns } from "../../SourceData";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import MessageModals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";

const ServicesView = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");

  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(10);

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Swipeable modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalView, setModalView] = useState("create");
  const [editServiceData, setEditServiceData] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Services data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      setPage(1);
    }
  };

  // Handle category change
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  // Fetch services data
  const fetchServices = async () => {
    if (!hasPermission(46)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      if (selectedCategory !== 10) {
        params.category = selectedCategory;
      }

      const response = await api.get("/services", { params });

      if (response.data.status === "success") {
        setServices(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare service data for editing
  const handleEditService = (serviceRow) => {
    if (!hasPermission(48)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setEditServiceData(serviceRow);
    setModalView("edit");
    setModalOpen(true);
  };

  // Fetch categories for the dropdown
  const fetchCategories = async () => {
    if (!hasPermission(46)) {
      return;
    }
    try {
      const response = await api.get("/categories", {
        params: {
          limit: 100,
        },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Effect to fetch services when dependencies change
  useEffect(() => {
    fetchServices();
  }, [page, pageSize, searchTerm, selectedCategory]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Toggle service active state
  const handleActive = async (id) => {
    if (!hasPermission(47)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/services/${id}/toggle`);
      toast.success("تم تغير الحالة بنجاح");
      fetchServices();
    } catch (error) {
      console.error("Error toggling service:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening create service modal
  const handleOpenCreateModal = () => {
    if (!hasPermission(45)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setModalView("create");
    setEditServiceData(null);
    setModalOpen(true);
  };

  // Handle delete service
  const handleDeleteService = (serviceRow) => {
    if (!hasPermission(49)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setSelectedService(serviceRow);
    setOpenDelete(true);
  };

  // Handle modal close and refresh data if needed
  const handleModalClose = (dataChanged = false) => {
    setModalOpen(false);
    if (dataChanged) {
      fetchServices();
    }
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;

        if (!hasPermission(47)) {
          return <span>{isActive ? "نشط" : "غير نشط"}</span>;
        }

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canEdit = hasPermission(48);
        const canDelete = hasPermission(49);

        if (!canEdit && !canDelete) {
          return <div className="cell-action"></div>;
        }

        return (
          <div className="cell-action">
            {canEdit && (
              <div
                className="cell-btn"
                onClick={() => handleEditService(params.row)}
              >
                <img src={EditUser} alt="تعديل" />
              </div>
            )}

            {canDelete && (
              <div
                className="cell-btn"
                onClick={() => handleDeleteService(params.row)}
              >
                <img src={DeleteUser} alt="حذف" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="special-header">
        <p className="title">
          الخدمات <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم الخدمة...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                الاقسام
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category?.name?.ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <BlueButton
            text="اضافة خدمات جديدة"
            size={isMobile ? "full" : isTablet ? "full" : "large"}
            variant="fill"
            onClick={handleOpenCreateModal}
            icon={<img src={AddGif} alt="" />}
          />
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={ServiceColumns.concat(actionColumn)}
          rows={services.map((service, index) => ({
            ...service,
            id: service._id || index,
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1);
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "خدمة في الصفحة",
            },
          }}
        />

        {(hasPermission(45) || hasPermission(48)) && (
          <SwipeableModals
            open={modalOpen}
            onClose={() => handleModalClose()}
            onOpen={() => setModalOpen(true)}
            currentView={modalView}
            editData={editServiceData}
            onSubmitSuccess={() => handleModalClose(true)}
            categories={categories}
          />
        )}

        {hasPermission(49) && (
          <MessageModals
            open={openDelete}
            currentView="delete"
            deleteMessage={"تم حذف الخدمة بنجاح"}
            route={selectedService ? `/services/${selectedService._id}` : ""}
            onDataChange={fetchServices}
            onClose={() => {
              setOpenDelete(false);
              setSelectedService(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default ServicesView;